import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api-client.js';
import { Button } from '../components/ui/button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { DataTable, type Column } from '../components/ui/data-table.js';
import { EmptyState } from '../components/ui/empty-state.js';
import { Input } from '../components/ui/input.js';

interface MessageTemplate {
  id: string;
  name: string;
  tts_voice?: string;
  intro_voice_message?: string;
  sms_body?: string;
  email_subject?: string;
  confirm_identity: boolean;
  created_at: string;
}

interface ListResponse {
  data: MessageTemplate[];
  pagination: { next_cursor: string | null; has_more: boolean };
}

export function MessageTemplatesPage(): JSX.Element {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const { data, isLoading, error } = useQuery({
    queryKey: ['message-templates'],
    queryFn: () => api.get('/v1/message-templates') as Promise<ListResponse>,
  });

  const filtered = useMemo(() => {
    if (!data) return [];
    const s = search.trim().toLowerCase();
    if (!s) return data.data;
    return data.data.filter((row) =>
      [row.name, row.tts_voice, row.email_subject, row.sms_body, row.intro_voice_message]
        .some((v) => v && v.toLowerCase().includes(s)),
    );
  }, [data, search]);

  const deleteOne = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/message-templates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['message-templates'] }),
  });

  const columns: Column<MessageTemplate>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (row) => <span className="font-medium">{row.name}</span>,
    },
    {
      key: 'channels',
      header: 'Channels',
      render: (row) => {
        const tags: string[] = [];
        if (row.intro_voice_message || row.tts_voice) tags.push('voice');
        if (row.sms_body) tags.push('sms');
        if (row.email_subject) tags.push('email');
        return (
          <div className="flex gap-1">
            {tags.length === 0 && <span className="text-ink-muted">—</span>}
            {tags.map((t) => (
              <span
                key={t}
                className="rounded bg-surface-alt border border-border text-ink-muted px-1.5 py-0.5 text-xs"
              >
                {t}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      key: 'voice',
      header: 'TTS voice',
      render: (row) => row.tts_voice ?? '—',
    },
    {
      key: 'identity',
      header: 'Confirm ID',
      render: (row) => (row.confirm_identity ? 'Yes' : 'No'),
      width: '110px',
    },
    {
      key: 'created',
      header: 'Created',
      render: (row) => new Date(row.created_at).toLocaleDateString(),
      width: '120px',
    },
    {
      key: 'actions',
      header: '',
      width: '160px',
      className: 'text-right',
      render: (row) => (
        <div className="flex justify-end gap-1">
          <Link to={`/message-templates/${row.id}/edit`}>
            <Button variant="ghost" size="sm">Edit</Button>
          </Link>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (window.confirm(`Delete template "${row.name}"?`)) deleteOne.mutate(row.id);
            }}
            disabled={deleteOne.isPending}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Message templates</h1>
          <p className="text-sm text-ink-muted">
            Reusable voice / SMS / email content with variable interpolation. Referenced by events
            and campaigns.
          </p>
        </div>
        <Link to="/message-templates/new">
          <Button>New template</Button>
        </Link>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>All templates</CardTitle>
          {data && (
            <span className="text-xs text-ink-muted">
              {search ? `${filtered.length} of ${data.data.length}` : data.data.length}
              {data.pagination.has_more && !search ? '+' : ''}
            </span>
          )}
        </CardHeader>
        <CardBody className="p-0">
          {data && (
            <div className="flex gap-2 px-4 py-3 border-b border-border">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, voice, subject, body…"
                className="flex-1"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="text-sm text-ink-muted hover:text-ink px-2"
                >
                  Clear
                </button>
              )}
            </div>
          )}
          {isLoading && <p className="p-4 text-sm text-ink-muted">Loading…</p>}
          {error && <ErrorBanner error={error} />}
          {data && (
            <DataTable
              data={filtered}
              columns={columns}
              rowKey={(r) => r.id}
              emptyState={
                search ? (
                  <EmptyState
                    title="No matches"
                    description={`Nothing matches "${search}". Try a different term or clear the search.`}
                  />
                ) : (
                  <EmptyState
                    title="No templates yet"
                    description="Create one with TTS voice + SMS body + email body to reuse across events and campaigns."
                  />
                )
              }
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function ErrorBanner({ error }: { error: unknown }): JSX.Element {
  const status = error instanceof ApiError ? error.status : 0;
  return (
    <div className="m-4 rounded border border-danger bg-surface p-3 text-sm">
      <p className="font-medium text-danger">Failed to load</p>
      <p className="mt-1 text-ink-muted">
        {status ? `HTTP ${status}` : 'Network error'} — {(error as Error).message}
      </p>
    </div>
  );
}
